/**
 * DemoContext — CaraLoom Controlled Demo Environment (iter 179).
 *
 * A single React context that owns:
 *   - the current demo state (deep-copied from /app/frontend/src/data/demoData.js)
 *   - `reset()` — restores INITIAL_STATE without touching production data
 *   - `presenterMode` — toggled by the Presenter Bar; UI-only
 *   - `focusMode` — hides secondary chrome for on-screen presentation
 *   - `access` — 'authenticated' | 'demo-token'; used to render "you're a
 *     prospect" vs "you're a presenter" chrome subtly differently
 *
 * IMPORTANT: No production API calls originate from this context. Any
 * button that "acts" inside the demo only mutates local state.
 */
import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { loadInitialDemoState } from '../data/demoData';
import { useAuth } from './AuthContext';

const DemoContext = createContext(null);

const DEMO_SESSION_KEY = 'caraloom_demo_access';   // sessionStorage key for prospect-preview token
const DEMO_ORG_KEY     = 'caraloom_demo_org';      // optional org name captured at booking time

export function DemoProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(loadInitialDemoState);
  const [presenterMode, setPresenterMode] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [personalizedOrg, setPersonalizedOrg] = useState(null);
  // Iter 179 · which archetype of Eleanor's story to render. Presenter
  // switches this from the header dropdown; no route change; no reset.
  const [archetype, setArchetype] = useState('home-health');

  // Personalized org from booking metadata (if any) — captured client-side.
  useEffect(() => {
    try {
      const org = sessionStorage.getItem(DEMO_ORG_KEY);
      if (org) setPersonalizedOrg(org);
    } catch { /* noop */ }
  }, []);

  const reset = useCallback(() => {
    setState(loadInitialDemoState());
    setArchetype('home-health');
  }, []);

  // Access model:
  //   * authenticated CaraLoom user → 'authenticated' (presenter or internal)
  //   * unauthenticated visitor with a demo access flag in sessionStorage →
  //     'demo-token' (prospect preview after booking)
  //   * neither → null (route guard should have already redirected)
  const access = useMemo(() => {
    if (user) return 'authenticated';
    try {
      const flag = sessionStorage.getItem(DEMO_SESSION_KEY);
      if (flag === 'granted') return 'demo-token';
    } catch { /* noop */ }
    return null;
  }, [user]);

  const value = useMemo(() => ({
    state,
    setState,
    reset,
    presenterMode,
    setPresenterMode,
    focusMode,
    setFocusMode,
    access,
    personalizedOrg,
    archetype,
    setArchetype,
  }), [state, reset, presenterMode, focusMode, access, personalizedOrg, archetype]);

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error('useDemo must be used inside <DemoProvider>');
  return ctx;
}

/** Grant demo access for prospect preview after a successful booking. */
export function grantDemoAccess({ org } = {}) {
  try {
    sessionStorage.setItem(DEMO_SESSION_KEY, 'granted');
    if (org) sessionStorage.setItem(DEMO_ORG_KEY, org);
  } catch { /* noop */ }
}

/** Called by the demo route guard to check if a visitor is allowed. */
export function hasDemoAccess({ user }) {
  if (user) return true;
  try {
    return sessionStorage.getItem(DEMO_SESSION_KEY) === 'granted';
  } catch {
    return false;
  }
}
